import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Loader } from '@mummssoftware/common-ui';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import { nameWithCredentials } from '@mummssoftware/utils/formatters';
import { weekdays } from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { transparent } from '@mummssoftware/utils/primitives';
import { filter, flowRight as compose, negate, property } from 'lodash-es';
import { Refused } from '@mummssoftware/utils/frequencies/frequency-constants';
import { PatientAssistanceContext } from '../../context';
import { AssistanceContext } from '../Context/AssistanceContext';

// from HCPCS Table
const useStyles = makeStyles({
  tableContainer: {
    width: 'fit-content',
  },
  table: {
    minWidth: 400,
    width: 'fit-content',
  },
  cell: {
    padding: '2px 10px',
    fontSize: '0.875rem',
  },
  headerCell: {
    padding: '2px 10px',
    fontSize: '0.875rem',
    fontWeight: 600,
    border: '1px solid gray',
    borderRadius: '5px',
  },
  rateCell: {
    textAlign: 'right',
  },
});

const extractDate = (dt: string) => dt.split(' ')[0];

const extractTime = (dt: string) =>
  dt.split(' ')[1].split(':').slice(0, 2).join(':');

const extractWeekday = (dt: string) => {
  const dayCode = parseDateMMDDYYYYSlashes(dt).getDay();
  // todo verify this does not get messed up with locale-specific configs
  return weekdays()[dayCode];
};

const getTaskDateTime = (taskAdmin: mumms.PatientCppTaskAdministrations) =>
  taskAdmin.patientvisitdatetime_shifted || taskAdmin.actiondatetime_shifted;

const getTaskStatus: (
  admin: mumms.PatientCppTaskAdministrations,
) => string = property('status');

const taskAdminToRow = (taskAdmin: mumms.PatientCppTaskAdministrations) => [
  extractDate(getTaskDateTime(taskAdmin)),
  extractTime(getTaskDateTime(taskAdmin)),
  extractWeekday(taskAdmin.actiondatetime),
  nameWithCredentials(
    taskAdmin.actionbypersonfirstname,
    taskAdmin.actionbypersonlastname,
    taskAdmin.actionbypersoncredentials,
  ),
  getTaskStatus(taskAdmin),
];

const adminIsRefused = (admin: mumms.PatientCppTaskAdministrations) =>
  admin.status === Refused;

const filterOutRefused = (admins: mumms.PatientCppTaskAdministrations[]) =>
  filter(admins, negate(adminIsRefused));

export const DetailsModal = ({
  task,
}: {
  task: (mumms.WeekSummaryTaskDto & mumms.WeekSummaryTaskWeek) | null;
}) => {
  const classes = useStyles();

  const { observation, agency } = useContext(PatientAssistanceContext);
  const { includeRefused } = useContext(AssistanceContext);

  const [taskAdmins, setTaskAdmins] = useState<
    null | mumms.PatientCppTaskAdministrations[]
  >(null);

  const applyFilter = !includeRefused ? filterOutRefused : transparent;

  useEffect(() => {
    if (task) {
      observation
        .getPatientTasksDetails(task.key, agency)
        .then(compose(setTaskAdmins, applyFilter));
    }
  }, [task]);

  const rowData = taskAdmins && taskAdmins.map(taskAdminToRow);

  // todo translations file
  return (
    <>
      {!taskAdmins ? (
        <div style={{ width: '420px', height: '50px' }}>
          <Loader />
        </div>
      ) : (
        <TableContainer className={classes.tableContainer} component={Paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Date</TableCell>
                <TableCell className={classes.headerCell}>Time</TableCell>
                <TableCell className={classes.headerCell}>Weekday</TableCell>
                <TableCell className={classes.headerCell}>Provider</TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowData &&
                rowData.map((row) => (
                  <TableRow>
                    {row.map((text) => (
                      <TableCell className={classes.cell}>{text}</TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
